import {
  GridApi,
  RowNode,
  ICellRendererParams
} from '@ag-grid-enterprise/all-modules';
import React, {
  FC,
  Fragment,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { SipPrefix } from 'data/sipSetting/sipSetting.types';
import CellValidationAlert from './CellValidationAlert';

type Props = {
  value: string,
  api: GridApi,
  context: {
    onCodeValidationError: (error: boolean) => void,
    sipPrefixOccurrences: { [key: string]: number },
    sipPrefixes: SipPrefix[]
  },
} & ICellRendererParams;

const CodeCellRenderer: FC<Props> = (props: Props) => {
  const {
    value, context, api, rowIndex
  } = props;
  const [isCodeValid, setCodeValid] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const { t } = useTranslation();

  useEffect(() => {

    if (!value) {
      setCodeValid(false);
      setErrorMessage(t('sipSetting.grid.prefixRequired'));
      context.onCodeValidationError(true);
      return;
    }

    const isPatternValid = (new RegExp(/^[a-zA-Z0-9]*$/)).test(value) && value.length > 0 && value.length <= 16;
    if (!isPatternValid) {
      context.onCodeValidationError(true);
      setCodeValid(false);
      setErrorMessage(t('sipSetting.grid.invalidCode'));
      return;
    }

    const values:string[] = [];
    api.forEachNode((node: RowNode) => {
      values.push(node.data.code);
    });

    const isPrefixRepeatedValid = values.filter((item) => item === value).length === 1;

    if (!isPrefixRepeatedValid) {
      context.onCodeValidationError(true);
      setCodeValid(false);
      setErrorMessage(t('sipSetting.grid.uniquePrefixValidator'));
      return;
    }
    setCodeValid(true);
    setErrorMessage('');
    context.onCodeValidationError(false);
  }, [value, context, api]);

  const renderInvalidNameAlert = () => {
    if (isCodeValid) {
      return null;
    }

    const tutorialIndex = context.sipPrefixes.findIndex(({ code }: SipPrefix) => code === '')
    const showTooltipTutorial = rowIndex === tutorialIndex;

    return (
      <Fragment>
        <div data-testid="CellValidationAlert">
          <CellValidationAlert
            msg={errorMessage}
            initialOpenStatus={showTooltipTutorial}
          />&nbsp;
        </div>
      </Fragment>
    );
  };

  return (
    <div>
      {renderInvalidNameAlert()}
      {value}
    </div>
  );
};

export default CodeCellRenderer;
